import React, {
  useEffect,
  useRef,
  useState,
  memo,
  useMemo,
  useCallback,
} from 'react';
import PropTypes from 'prop-types';
import { Calendar, DateObject } from 'react-multi-date-picker';
import gregorian from 'react-date-object/calendars/gregorian';
import gregorian_en from 'react-date-object/locales/gregorian_en';
import jmoment from 'moment-jalaali';
import classNames from 'classnames';
import weekends from 'react-multi-date-picker/plugins/highlight_weekends';
import 'react-multi-date-picker/styles/colors/teal.css';
import UiIcon from 'ui-components/ui-icon/UiIcon';
import { useTranslation } from 'react-i18next';
import { UiFormControlLabel, UiRadio, UiRadioGroup } from 'ui-components';
import {
  magicNumbers,
  timeDurationTools,
  mapCalendarToCalendars,
  mapCalendarToFormats,
  mapCalendarToLocales,
  mapCalendarToWeekDays,
  getTimestamp,
} from 'utils';
import { useWindowDimension } from 'hooks';
import { useDispatch, useSelector } from 'react-redux';
import {
  setDaysAgo,
  setSelectedRangePicker,
  setLastSelectedRangePicker,
} from 'store';
import {
  TimeDuration,
  TimePicker,
  RangePickerFooter,
  DisplaySelectedDateTime,
  DaysAgo,
} from './components';
import {
  StyledRangePicker,
  StyledRangePickerPopover,
} from './JalaliRangePicker.styles';

/**
 * A comprehensive date range picker component supporting both Jalali (Persian) and Gregorian calendars.
 * Provides multiple selection modes including predefined durations, days ago selection, and calendar picking.
 *
 * @component
 * @param {Object} props - Component props
 * @param {Function} [props.onChange] - Callback function called when date range changes. Receives array of timestamps or duration string
 * @param {Object} [props.dateTime] - Initial date time object with fromDate and toDate timestamps
 * @param {number} [props.dateTime.fromDate] - Start date timestamp
 * @param {number} [props.dateTime.toDate] - End date timestamp
 * @param {string} [props.duration] - Initial duration string (e.g., '1d', '7d', '30d')
 * @param {Object} [props.minDate] - Minimum date configuration
 * @param {Object} [props.maxDate] - Maximum date configuration
 * @param {number} [props.minDate.fromNow] - Number of units from now for minimum date
 * @param {string} [props.minDate.unit] - Unit type for minimum date ('days', 'months', etc.)
 * @param {boolean} [props.portal] - Whether to render calendar in a portal
 * @param {number} [props.offsetY] - Vertical offset for calendar positioning
 * @param {string} [props.selectedCalendar] - Calendar type ('jalali' or 'gregorian')
 * @param {boolean} [props.disabled] - Whether the component is disabled
 * @param {boolean} [props.presetDurationsOnly] - Hide days-ago and calendar modes; show preset chips only
 * @param {string} [props['data-testid']] - Test ID for testing purposes
 * @returns {JSX.Element} The JalaliRangePicker component
 *
 * @example
 * <JalaliRangePicker
 *   onChange={(dateRange) => console.info(dateRange)}
 *   selectedCalendar="jalali"
 *   duration="7d"
 *   data-testid="date-picker"
 * />
 */
const JalaliRangePicker = ({
  onChange,
  dateTime,
  duration,
  minDate,
  maxDate,
  portal,
  offsetY,
  selectedCalendar,
  disabled,
  presetDurationsOnly = false,
  ...props
}) => {
  const dispatch = useDispatch();
  const daysAgo = useSelector((state) => state.pageReducer.daysAgo);
  const selectedRangePicker = useSelector(
    (state) => state.pageReducer.selectedRangePicker
  );
  const lastSelectedRangePicker = useSelector(
    (state) => state.pageReducer.lastSelectedRangePicker
  );

  const { t } = useTranslation();
  const [, setForceUpdate] = useState(0);
  const [selectedDuration, setSelectedDuration] = useState(duration);
  const [isDaysAgoChanged, setIsDaysAgoChanged] = useState(false);
  let date = timeDurationTools(selectedDuration);
  const now = new DateObject({
    calendar: mapCalendarToCalendars[selectedCalendar],
    locale: mapCalendarToLocales[selectedCalendar],
  });
  const dateTimes = [
    new DateObject({
      date: dateTime?.fromDate * magicNumbers.CONVERT_TIMESTAMP_TO_DATE_FACTOR,
      calendar: mapCalendarToCalendars[selectedCalendar],
      locale: mapCalendarToLocales[selectedCalendar],
    }),
    new DateObject({
      date: dateTime?.toDate * magicNumbers.CONVERT_TIMESTAMP_TO_DATE_FACTOR,
      calendar: mapCalendarToCalendars[selectedCalendar],
      locale: mapCalendarToLocales[selectedCalendar],
    }),
  ];
  const [values, setValues] = useState(
    duration ? [date.from, date.to] : dateTimes
  );
  const [fromTime, setFromTime] = useState(
    duration ? new DateObject(date.from) : new DateObject()
  );
  const [toTime, setToTime] = useState(new DateObject());
  const datePickerRef = useRef();
  const { width } = useWindowDimension();
  const isMobile = width <= 576;

  const [anchorEl, setAnchorEl] = useState(null);
  const isOpen = Boolean(anchorEl);

  /**
   * Initializes the component state from props
   * Resets all date/time values to match the provided props
   *
   * @callback initializeDateTimeFromProps
   */
  const initializeDateTimeFromProps = useCallback(() => {
    setSelectedDuration(duration);
    date = timeDurationTools(duration);
    setValues(duration ? [date.from, date.to] : dateTimes);
    setFromTime(
      duration ? new DateObject(date.from) : new DateObject(dateTimes?.[0])
    );
    setToTime(duration ? new DateObject() : new DateObject(dateTimes?.[1]));
  }, [dateTime, duration]);

  useEffect(() => {
    initializeDateTimeFromProps();
  }, [initializeDateTimeFromProps]);

  useEffect(() => {
    if (isOpen && !presetDurationsOnly) {
      dispatch(
        setSelectedRangePicker(lastSelectedRangePicker || 'defaultDurations')
      );
    }
  }, [isOpen]);

  /**
   * Opens the range picker popover
   *
   * @param {Event} event - The click event
   */
  const handleOpenRangePicker = (event) => {
    setAnchorEl(event.currentTarget);
  };

  /**
   * Closes the range picker popover
   * If event is provided, resets the component to initial state
   *
   * @param {Event} [event] - The close event
   */
  const handleCloseRangePicker = (event) => {
    if (event) {
      initializeDateTimeFromProps();
    }
    setAnchorEl(null);
  };

  /**
   * Submits the selected date range to the parent component
   * Converts the selected dates to timestamps and calls onChange callback
   * Handles both duration-based and date-based selections
   */
  const handleSubmit = () => {
    if (typeof onChange === 'function') {
      let finalDate = null;
      if (values[2]) {
        finalDate = [null, null, values[2]];
      } else {
        finalDate = [
          getTimestamp(
            `${new DateObject(values[0])
              .convert(gregorian, gregorian_en)
              .format('YYYY-MM-DD')} ${new DateObject(fromTime)
              .convert(gregorian, gregorian_en)
              .format('HH:mm')}`
          ),
          getTimestamp(
            `${new DateObject(values[1])
              .convert(gregorian, gregorian_en)
              .format('YYYY-MM-DD')} ${new DateObject(toTime)
              .convert(gregorian, gregorian_en)
              .format('HH:mm')}`
          ),
        ];
      }
      dispatch(
        setLastSelectedRangePicker(isDaysAgoChanged ? 'daysAgo' : 'calendar')
      );
      onChange(finalDate);
    }
  };

  /**
   * Handles selection of a predefined time duration
   * Updates all relevant state and immediately applies the selection
   *
   * @param {Object} item - The selected duration item
   * @param {string} item.time_duration - The duration string (e.g., '7d', '30d')
   */
  const handleTimeDurationChange = (item) => {
    values[2] = item.time_duration;
    setFromTime(new DateObject(timeDurationTools(item.time_duration).from));
    setToTime(now);
    setValues(() => [
      new DateObject(timeDurationTools(item.time_duration).from),
      new DateObject(now),
    ]);
    setSelectedDuration(item.time_duration);
    setForceUpdate((x) => x + 1);
    handleSubmit();
    handleCloseRangePicker();
    dispatch(setLastSelectedRangePicker(selectedRangePicker));
  };

  /**
   * Handles time component changes
   * Clears the selected duration when custom time is set
   */
  const handleTimeChange = () => {
    setSelectedDuration(undefined);
    setForceUpdate((x) => x + 1);
  };

  /**
   * Applies the current date/time selection
   * Closes the popover and submits the values
   */
  const handleDateTimeApply = () => {
    handleCloseRangePicker();
    handleSubmit();
  };

  /**
   * Handles 'days ago' selection
   * Creates a date range from N days ago to now
   *
   * @param {number} days - Number of days ago to set as start date
   */
  const handleDaysAgoChange = (days) => {
    setIsDaysAgoChanged(true);
    dispatch(setDaysAgo(days));
    const from = new DateObject({
      date: new DateObject(now).subtract(days, 'days').toDate(),
      calendar: mapCalendarToCalendars[selectedCalendar],
      locale: mapCalendarToLocales[selectedCalendar],
    });
    const to = new DateObject({
      calendar: mapCalendarToCalendars[selectedCalendar],
      locale: mapCalendarToLocales[selectedCalendar],
    });
    setValues([from, to]);
    setFromTime(new DateObject(from).setHour(0).setMinute(0));
    setToTime(now);
    setForceUpdate((x) => x + 1);
  };

  /**
   * Sets the end date/time to current moment
   * Keeps the start date unchanged
   */
  const handleToNow = () => {
    setValues([values[0], now]);
    setToTime(now);
    setForceUpdate((x) => x + 1);
  };

  /**
   * Checks if a given date is equal to today
   *
   * @param {DateObject} date - The date to check
   * @returns {boolean} True if the date is today
   */
  const isEqualToday = useCallback(
    (date) => {
      return (
        date.year === now.year &&
        date.month.number === now.month.number &&
        date.day === now.day
      );
    },
    [now]
  );

  /**
   * Handles date selection from the calendar
   * Sets appropriate time values based on whether end date is today or not
   *
   * @param {DateObject[]} date - Array containing start and end dates
   */
  const handleCalendarDateChange = useCallback(
    (date) => {
      setFromTime(new DateObject(date[0]).setHour(0).setMinute(0));
      setIsDaysAgoChanged(false);

      if (isEqualToday(new DateObject(date[1]))) {
        setToTime(now);
      } else {
        setToTime(new DateObject(date[1]).setHour(23).setMinute(59));
      }

      setValues(date);
      setSelectedDuration(undefined);
      setForceUpdate((x) => x + 1);
    },
    [isEqualToday, now]
  );

  const updateTriggers = useMemo(
    () => [selectedRangePicker, isOpen],
    [selectedRangePicker, isOpen]
  );

  /**
   * Memoized calendar plugins including weekend highlighting and time picker
   *
   * @type {Array} Array of calendar plugins
   */
  const plugins = useMemo(
    () => [
      weekends(),
      // eslint-disable-next-line react/jsx-key
      <TimePicker
        fromTime={fromTime}
        toTime={toTime}
        values={values}
        selectedCalendar={selectedCalendar}
        onTimeChange={handleTimeChange}
        position='bottom'
        data-testid={props?.['data-testid']}
        handleToNow={handleToNow}
      />,
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dateTime, props?.duration, values, minDate]
  );

  return (
    <StyledRangePicker
      className={classNames(
        'range-picker flex',
        isMobile ? 'is-mobile' : 'is-desktop'
      )}
      data-testid={props?.['data-testid']}
    >
      <div
        className='range-picker-wrapper flex between date-time'
        onClick={!disabled && handleOpenRangePicker}
      >
        <DisplaySelectedDateTime
          fromTime={fromTime}
          toTime={toTime}
          value={values}
        />

        <UiIcon
          icon='ri-calendar-2-line'
          className='mr-0'
          size={20}
          data-testid={`${props?.['data-testid']}-icon`}
          id={`${props?.['data-testid']}-btn`}
        />
      </div>

      <StyledRangePickerPopover
        updateTriggers={updateTriggers}
        id={isOpen ? 'range-picker-popover' : undefined}
        open={isOpen}
        anchorEl={anchorEl}
        onClose={handleCloseRangePicker}
        paperStyles={{
          borderRadius: '8px',
          width: isMobile ? '350px' : '560px',
          marginTop: '4px',
        }}
      >
        <div>
          <div style={{ padding: isMobile ? '8px' : '16px' }}>
            {presetDurationsOnly ? (
              <div className='teal preset-durations'>
                <TimeDuration
                  onTimeDurationChange={handleTimeDurationChange}
                  selectedTimeDuration={selectedDuration}
                  minDate={minDate}
                  data-testid={props?.['data-testid']}
                />
              </div>
            ) : (
              <UiRadioGroup
                value={selectedRangePicker}
                onChange={(e) =>
                  dispatch(setSelectedRangePicker(e.target.value))
                }
              >
                <div className='teal'>
                  <UiFormControlLabel
                    style={{ marginRight: 0 }}
                    className='radio-text'
                    label={t('common:defaultDurations')}
                    control={<UiRadio value={'defaultDurations'} />}
                  />
                  {selectedRangePicker === 'defaultDurations' && (
                    <TimeDuration
                      onTimeDurationChange={handleTimeDurationChange}
                      selectedTimeDuration={selectedDuration}
                      minDate={minDate}
                      data-testid={props?.['data-testid']}
                    />
                  )}
                </div>

                <div
                  className='flex align-center gap-8'
                  style={{
                    padding: `${isMobile ? '8px' : '16px'} 0`,
                  }}
                >
                  <UiFormControlLabel
                    style={{ marginRight: 0 }}
                    className='radio-text'
                    control={<UiRadio value={'daysAgo'} />}
                  />
                  <DaysAgo
                    daysAgo={daysAgo}
                    handleDaysAgoChange={handleDaysAgoChange}
                    isDisabled={selectedRangePicker !== 'daysAgo'}
                    maxDay={30}
                  />
                </div>

                <div>
                  <UiFormControlLabel
                    className='radio-text'
                    label={t('common:selectFromCalendar')}
                    style={{ marginRight: 0 }}
                    control={
                      <UiRadio
                        onChange={(e) =>
                          dispatch(setSelectedRangePicker(e.target.value))
                        }
                        value={'calendar'}
                      />
                    }
                  />
                  {selectedRangePicker === 'calendar' && (
                    <Calendar
                      className='teal'
                      range={true}
                      numberOfMonths={isMobile ? 1 : 2}
                      portal={portal}
                      inputClass='input'
                      editable={false}
                      ref={datePickerRef}
                      calendar={mapCalendarToCalendars[selectedCalendar]}
                      locale={mapCalendarToLocales[selectedCalendar]}
                      value={values}
                      onChange={handleCalendarDateChange}
                      offsetY={offsetY}
                      weekDays={mapCalendarToWeekDays[selectedCalendar]}
                      minDate={jmoment()
                        .subtract(
                          minDate?.fromNow || magicNumbers.LAST_FORTY_DAYS,
                          minDate?.unit || 'days'
                        )
                        .format(mapCalendarToFormats[selectedCalendar])}
                      maxDate={jmoment(maxDate || undefined).format(
                        mapCalendarToFormats[selectedCalendar]
                      )}
                      plugins={plugins}
                    />
                  )}
                </div>
              </UiRadioGroup>
            )}
          </div>
          <RangePickerFooter
            fromTime={fromTime}
            toTime={toTime}
            values={values}
            onDateTimeApply={handleDateTimeApply}
            position='bottom'
            data-testid={props?.['data-testid']}
          />
        </div>
      </StyledRangePickerPopover>
    </StyledRangePicker>
  );
};

JalaliRangePicker.propTypes = {
  onChange: PropTypes.func,
  duration: PropTypes.string,
  dateTime: PropTypes.shape({
    fromDate: PropTypes.number,
    toDate: PropTypes.number,
  }),
  minDate: PropTypes.any,
  maxDate: PropTypes.any,
  portal: PropTypes.bool,
  offsetY: PropTypes.number,
  'data-testid': PropTypes.string,
  selectedCalendar: PropTypes.string,
  disabled: PropTypes.bool,
  presetDurationsOnly: PropTypes.bool,
};

export default memo(JalaliRangePicker);
